/**
 * Milestone definitions and checking logic.
 */

import { supabase } from './supabase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createLogger } from '@/lib/logger';

const log = createLogger('MILESTONES');

const ACHIEVED_KEY = 'duel_milestones_achieved';

export interface MilestoneDef {
  id: string;
  label: string;
  emoji: string;
  description: string;
}

export const MILESTONES: MilestoneDef[] = [
  { id: 'first_workout', label: 'First Workout', emoji: '🏋️', description: 'You logged your first workout!' },
  { id: 'workout_10', label: '10 Workouts', emoji: '💪', description: '10 workouts down — building a habit!' },
  { id: 'workout_50', label: '50 Workouts', emoji: '🔥', description: '50 workouts! You\'re on fire.' },
  { id: 'workout_100', label: '100 Workouts', emoji: '💯', description: 'Century club! 100 workouts logged.' },
  { id: 'first_duel_win', label: 'First Duel Win', emoji: '⚔️', description: 'You won your first duel!' },
  { id: 'duel_wins_10', label: '10 Duel Wins', emoji: '🏆', description: '10 duel victories — a true competitor.' },
  { id: 'first_pr', label: 'First PR', emoji: '🚀', description: 'Personal record achieved!' },
  { id: 'baseline_complete', label: 'Core 5 Complete', emoji: '✅', description: 'All Core 5 lifts logged — you\'re ranked!' },
];

/** Get set of already-achieved milestone IDs from AsyncStorage */
async function getAchieved(): Promise<Set<string>> {
  try {
    const raw = await AsyncStorage.getItem(ACHIEVED_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch { /* fresh */ }
  return new Set();
}

async function markAchieved(ids: string[]): Promise<void> {
  const current = await getAchieved();
  ids.forEach((id) => current.add(id));
  await AsyncStorage.setItem(ACHIEVED_KEY, JSON.stringify([...current]));
}

export interface MilestoneCheckResult {
  newMilestones: MilestoneDef[];
}

/**
 * Check for newly achieved milestones after a workout save.
 * Returns only milestones that are newly achieved (not previously seen).
 */
export async function checkMilestones(userId: string): Promise<MilestoneCheckResult> {
  const achieved = await getAchieved();
  const newlyAchieved: MilestoneDef[] = [];

  try {
    // Workout count
    const { count: workoutCount } = await supabase
      .from('workouts')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId);

    const wc = workoutCount ?? 0;

    if (wc >= 1 && !achieved.has('first_workout')) {
      newlyAchieved.push(MILESTONES.find((m) => m.id === 'first_workout')!);
    }
    if (wc >= 10 && !achieved.has('workout_10')) {
      newlyAchieved.push(MILESTONES.find((m) => m.id === 'workout_10')!);
    }
    if (wc >= 50 && !achieved.has('workout_50')) {
      newlyAchieved.push(MILESTONES.find((m) => m.id === 'workout_50')!);
    }
    if (wc >= 100 && !achieved.has('workout_100')) {
      newlyAchieved.push(MILESTONES.find((m) => m.id === 'workout_100')!);
    }

    // Duel wins
    const { count: duelWins } = await supabase
      .from('duels')
      .select('id', { count: 'exact', head: true })
      .eq('winner_user_id', userId)
      .in('status', ['completed', 'resolved']);

    const dw = duelWins ?? 0;
    if (dw >= 1 && !achieved.has('first_duel_win')) {
      newlyAchieved.push(MILESTONES.find((m) => m.id === 'first_duel_win')!);
    }
    if (dw >= 10 && !achieved.has('duel_wins_10')) {
      newlyAchieved.push(MILESTONES.find((m) => m.id === 'duel_wins_10')!);
    }

    // PR check — any post with post_type = 'pr'
    const { count: prCount } = await supabase
      .from('posts')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('post_type', 'pr');

    if ((prCount ?? 0) >= 1 && !achieved.has('first_pr')) {
      newlyAchieved.push(MILESTONES.find((m) => m.id === 'first_pr')!);
    }

    // Baseline complete — check profile strength score
    const { data: prof } = await supabase
      .from('profiles')
      .select('duel_standard_strength_kg')
      .eq('id', userId)
      .maybeSingle();

    if (prof?.duel_standard_strength_kg && prof.duel_standard_strength_kg > 0 && !achieved.has('baseline_complete')) {
      newlyAchieved.push(MILESTONES.find((m) => m.id === 'baseline_complete')!);
    }

    // Mark newly achieved
    if (newlyAchieved.length > 0) {
      await markAchieved(newlyAchieved.map((m) => m.id));
    }
  } catch (err) {
    log.error('Milestone check failed:', err);
  }

  return { newMilestones: newlyAchieved };
}
